import axios from 'axios';
import { isByokActive, geminiText } from './geminiByokService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Shot Anchors ────────────────────────────────────────

/** Get shot anchors for a scene */
export async function getShotAnchors(projectId, sceneNumber) {
  const { data } = await axios.get(`${API}/shot-anchors/${projectId}/${sceneNumber}`);
  return data;
}

/** Create a new shot anchor */
export async function createShotAnchor(anchorData) {
  const { data } = await axios.post(`${API}/shot-anchors`, anchorData);
  return data;
}

/** Delete a shot anchor */
export async function deleteShotAnchor(anchorId) {
  const { data } = await axios.delete(`${API}/shot-anchors/${anchorId}`);
  return data;
}

/** Auto-plan shot anchors for a scene (AI) */
export async function autoPlanAnchors(planData) {
  if (isByokActive() && planData.scene_text) {
    return await autoPlanAnchorsByok(planData);
  }
  const { data } = await axios.post(`${API}/shot-anchors/auto-plan`, planData);
  return data;
}

async function autoPlanAnchorsByok(planData) {
  const systemPrompt = `You are a professional cinematographer and storyboard artist. Plan shot anchors for a scene.`;
  const userPrompt = `Given this scene, suggest shot anchors (key moments that need specific camera setups). Return JSON array:
[{"line_text": "quoted line from scene", "shot_type": "wide|medium|close-up|extreme_close_up|over_shoulder", "description": "what this shot captures", "camera_angle": "eye_level|low|high|dutch|birds_eye", "movement": "static|pan|tilt|dolly|tracking|crane"}]

SCENE:
${planData.scene_text}

Return ONLY valid JSON array.`;

  const text = await geminiText(systemPrompt, userPrompt);
  const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
  return { anchors: JSON.parse(cleaned), source: 'byok' };
}

/** Get AI-suggested camera settings */
export async function suggestSettings(settingsData) {
  if (isByokActive() && settingsData.description) {
    return await suggestSettingsByok(settingsData);
  }
  const { data } = await axios.post(`${API}/shot-anchors/suggest-settings`, settingsData);
  return data;
}

async function suggestSettingsByok(settingsData) {
  const systemPrompt = `You are an expert cinematographer. Suggest camera settings for a shot.`;
  const userPrompt = `Suggest camera settings for this shot description:
"${settingsData.description}"

Return JSON: {"shot_type": "", "camera_angle": "", "focal_length": "", "movement": "", "lighting_notes": "", "composition_notes": ""}

Return ONLY valid JSON.`;

  const text = await geminiText(systemPrompt, userPrompt);
  const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
  return JSON.parse(cleaned);
}

/** Create a shot within an anchor */
export async function createAnchorShot(anchorId, shotData) {
  const { data } = await axios.post(`${API}/shot-anchors/${anchorId}/shots`, shotData);
  return data;
}

/** Chat/refine an anchor shot (AI) */
export async function chatAnchorShot(anchorId, shotId, messageData) {
  const { data } = await axios.post(`${API}/shot-anchors/${anchorId}/shots/${shotId}/chat`, messageData);
  return data;
}

/** Delete a shot from an anchor */
export async function deleteAnchorShot(anchorId, shotId) {
  const { data } = await axios.delete(`${API}/shot-anchors/${anchorId}/shots/${shotId}`);
  return data;
}

// ─── Segment Shots ───────────────────────────────────────

/** Get segment shots for a project */
export async function getSegmentShots(projectId) {
  const { data } = await axios.get(`${API}/segment-shots/${projectId}`);
  return data;
}

/** Create a new segment shot */
export async function createSegmentShot(shotData) {
  const { data } = await axios.post(`${API}/segment-shots`, shotData);
  return data;
}

/** Chat/refine a segment shot (AI) */
export async function chatSegmentShot(shotId, chatData) {
  const { data } = await axios.post(`${API}/segment-shots/${shotId}/chat`, chatData);
  return data;
}

/** Restore a segment shot to a previous version */
export async function restoreSegmentShotVersion(shotId, versionData) {
  const { data } = await axios.post(`${API}/segment-shots/${shotId}/restore-version`, versionData);
  return data;
}

/** Delete a segment shot */
export async function deleteSegmentShot(shotId) {
  const { data } = await axios.delete(`${API}/segment-shots/${shotId}`);
  return data;
}

// ─── Storyboard ──────────────────────────────────────────

/** Get storyboard frames for a project (metadata only, images loaded separately) */
export async function getStoryboardFrames(projectId) {
  const { data } = await axios.get(`${API}/storyboard/frames?project_id=${projectId}`);
  return data;
}

/** Get the URL for a frame's image (served as raw PNG) */
export function getFrameImageUrl(frameId) {
  return `${API}/storyboard/frames/${frameId}/image`;
}

/** Get the URL for a shot's image within an anchor (served as raw PNG) */
export function getShotImageUrl(anchorId, shotId) {
  return `${API}/shot-anchors/shots/${anchorId}/${shotId}/image`;
}

/** Get the original script text for a scene */
export async function getSceneContext(projectId, sceneNumber) {
  const { data } = await axios.get(`${API}/storyboard/scene-context/${projectId}/${sceneNumber}`);
  return data;
}

/** Generate storyboard frames (AI) */
export async function generateStoryboard(genData) {
  const { data } = await axios.post(`${API}/storyboard/generate`, genData);
  return data;
}

/** Insert a shot into the storyboard */
export async function insertShot(shotData) {
  const { data } = await axios.post(`${API}/storyboard/insert-shot`, shotData);
  return data;
}

/** Update a storyboard frame — accepts full frame data object */
export async function updateFrame(frameId, frameData) {
  const { data } = await axios.put(`${API}/storyboard/frames/${frameId}`, frameData);
  return data;
}

/** Regenerate a storyboard frame with prompt and optional equipment/reference */
export async function regenerateFrame(frameId, payload) {
  // Accept either string (backward compat) or object
  const body = typeof payload === "string" ? { prompt: payload } : payload;
  const { data } = await axios.put(`${API}/storyboard/${frameId}/regenerate`, body);
  return data;
}

/** Delete a storyboard frame */
export async function deleteFrame(frameId) {
  const { data } = await axios.delete(`${API}/storyboard/frame/${frameId}`);
  return data;
}

/** Clear all storyboard frames for a project */
export async function clearStoryboard(projectId) {
  const { data } = await axios.delete(`${API}/storyboard/clear/${projectId}`);
  return data;
}

/** Get structured shot list */
export async function getShotList(projectId) {
  const { data } = await axios.get(`${API}/storyboard/${projectId}/shot-list`);
  return data;
}

/** Export shot list as CSV (returns raw text) */
export async function exportShotListCsv(projectId) {
  const { data } = await axios.get(`${API}/storyboard/${projectId}/shot-list/csv`);
  return data;
}

// ─── Shot Versions ───────────────────────────────────────

/** Get version history for a frame */
export async function getShotVersions(projectId, frameId) {
  const { data } = await axios.get(`${API}/shot-versions/${projectId}/frames/${frameId}/versions`);
  return data;
}

/** Save a new version of a frame */
export async function createShotVersion(projectId, frameId, versionData) {
  const { data } = await axios.post(`${API}/shot-versions/${projectId}/frames/${frameId}/versions`, versionData);
  return data;
}

/** Restore a frame to a previous version */
export async function restoreShotVersion(projectId, frameId, versionId) {
  const { data } = await axios.post(`${API}/shot-versions/${projectId}/frames/${frameId}/versions/${versionId}/restore`, {});
  return data;
}

/** Delete a shot version */
export async function deleteShotVersion(projectId, frameId, versionId) {
  const { data } = await axios.delete(`${API}/shot-versions/${projectId}/frames/${frameId}/versions/${versionId}`);
  return data;
}

// ─── Shot Concepts ──────────────────────────────────────

/** Get all concepts for a shot anchor */
export async function getConceptsForShot(projectId, anchorId) {
  const { data } = await axios.get(`${API}/shot-concepts/${projectId}/anchor/${anchorId}`);
  return data;
}

/** Get all selected concepts for a project (one per anchor) */
export async function getSelectedConcepts(projectId) {
  const { data } = await axios.get(`${API}/shot-concepts/${projectId}/selected`);
  return data;
}

/** Create a new concept for a shot anchor */
export async function createConcept(conceptData) {
  const { data } = await axios.post(`${API}/shot-concepts`, conceptData);
  return data;
}

/** Select a concept as the winner for its shot */
export async function selectConcept(conceptId) {
  const { data } = await axios.post(`${API}/shot-concepts/${conceptId}/select`);
  return data;
}

/** Generate or regenerate image for a concept */
export async function generateConceptImage(conceptId, payload) {
  const { data } = await axios.post(`${API}/shot-concepts/${conceptId}/generate`, payload);
  return data;
}

/** Chat-edit a concept's image */
export async function chatEditConcept(conceptId, message) {
  const { data } = await axios.post(`${API}/shot-concepts/${conceptId}/chat`, { message });
  return data;
}

/** Update concept name or settings */
export async function updateConcept(conceptId, updates) {
  const { data } = await axios.put(`${API}/shot-concepts/${conceptId}`, updates);
  return data;
}

/** Delete a concept */
export async function deleteConcept(conceptId) {
  const { data } = await axios.delete(`${API}/shot-concepts/${conceptId}`);
  return data;
}

/** Get concept image URL (served as raw PNG) */
export function getConceptImageUrl(conceptId) {
  return `${API}/shot-concepts/${conceptId}/image`;
}

/** Get concept version history */
export async function getConceptVersions(projectId, conceptId) {
  const { data } = await axios.get(`${API}/shot-versions/${projectId}/concepts/${conceptId}/versions`);
  return data;
}

/** Migrate project to concept layer */
export async function migrateProjectConcepts(projectId) {
  const { data } = await axios.post(`${API}/shot-concepts/migrate/${projectId}`);
  return data;
}

// ─── Style Ladder ────────────────────────────────────────

/** Upgrade a frame's rendering style */
export async function upgradeFrameStyle(frameId, targetStyle) {
  const { data } = await axios.post(`${API}/storyboard/frames/${frameId}/upgrade-style`, {
    target_style: targetStyle,
  });
  return data;
}

/** Batch upgrade multiple frames to a target style */
export async function batchUpgradeStyle(frameIds, targetStyle) {
  const { data } = await axios.post(`${API}/storyboard/batch-upgrade-style`, {
    frame_ids: frameIds,
    target_style: targetStyle,
  });
  return data;
}

// ─── Auto Storyboard ─────────────────────────────────────

/** Get pre-flight check for auto storyboard */
export async function getAutoStoryboardPreflight(projectId, sceneNumbers = null) {
  const params = sceneNumbers ? `?scenes=${sceneNumbers.join(',')}` : '';
  const { data } = await axios.get(`${API}/auto-storyboard/preflight/${projectId}${params}`);
  return data;
}

/** Auto-generate storyboard for a single scene */
export async function autoGenerateScene(projectId, sceneNumber, clearExisting = false) {
  const { data } = await axios.post(`${API}/auto-storyboard/scene`, {
    project_id: projectId,
    scene_number: sceneNumber,
    clear_existing: clearExisting
  });
  return data;
}

/** Auto-generate storyboard for entire movie */
export async function autoGenerateFull(projectId, startScene = 1, endScene = null, clearExisting = false) {
  const { data } = await axios.post(`${API}/auto-storyboard/full`, {
    project_id: projectId,
    start_scene: startScene,
    end_scene: endScene,
    clear_existing: clearExisting
  });
  return data;
}

/** Get auto-storyboard generation progress */
export async function getAutoStoryboardProgress(projectId) {
  const { data } = await axios.get(`${API}/auto-storyboard/progress/${projectId}`);
  return data;
}

/** Cancel auto-storyboard generation */
export async function cancelAutoStoryboard(projectId) {
  const { data } = await axios.post(`${API}/auto-storyboard/cancel/${projectId}`);
  return data;
}

// ─── Batch Generation ────────────────────────────────────

/** Batch generate character sheets */
export async function batchGenerateSheets(projectId, characterIds, style = 'photorealistic') {
  const { data } = await axios.post(`${API}/characters/batch-generate-sheets`, {
    project_id: projectId,
    character_ids: characterIds,
    style,
  });
  return data;
}

/** Batch generate location references */
export async function batchGenerateLocationRefs(projectId, locationIds) {
  const { data } = await axios.post(`${API}/locations/batch-generate-references`, {
    project_id: projectId,
    location_ids: locationIds,
  });
  return data;
}
